import { X, Search, CalendarIcon } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { vi } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useAccessibleProjectTypes, useProjectRoleUsers, useProjectRoleTypes } from '@/hooks/useAccessibleFilterOptions';
import { useAccessibleContractYears } from '@/hooks/useAccessibleContractYears';
import { useGlobalFeatureFlag } from '@/lib/featureFlags';
import { PROJECT_MARKS, PROJECT_MARK_LABELS, PROJECT_MARK_BADGE_COLORS } from '@/constants/projectMarks';

import type { MaintenanceFiltersData } from '@/hooks/maintenance/useMaintenanceFiltersState';
import { MultiSelect } from '@/components/ui/multi-select';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatDateForBackend } from '@/utils/dateFormatUtils';
import { cn } from '@/lib/utils';


interface MaintenanceFiltersProps {
  filters: MaintenanceFiltersData;
  onFiltersChange: (filters: MaintenanceFiltersData) => void;
  isLoading?: boolean;
  rightSlot?: React.ReactNode;
}

export default function MaintenanceFilters({ filters, onFiltersChange, isLoading = false, rightSlot }: MaintenanceFiltersProps) {
  const isProjectRolesEnabled = useGlobalFeatureFlag('ENABLE_PROJECT_ROLES');
  
  const { data: accessibleTypes, isLoading: typesLoading } = useAccessibleProjectTypes();
  const { data: contractYearOptions = [] } = useAccessibleContractYears();
  const { data: projectRoleUsers, isLoading: roleUsersLoading } = useProjectRoleUsers();
  const { data: projectRoleTypes, isLoading: roleTypesLoading } = useProjectRoleTypes();

  const handleFilterChange = (key: keyof MaintenanceFiltersData, value: string | string[]) => {
    onFiltersChange({
      ...filters,
      [key]: value
    });
  };

  const clearFilter = (key: keyof MaintenanceFiltersData) => {
    if (key === 'projectType' || key === 'contractYear') {
      handleFilterChange(key, []);
    } else {
      handleFilterChange(key, '');
    }
  };

  const clearAllFilters = () => {
    onFiltersChange({
      searchQuery: '',
      projectGroup: '',
      projectType: [],
      teamLead: '',
      company: '',
      projectMark: '',
      projectRoleUserIds: [],
      projectRoleTypes: [],
      lifecycleStage: '',
      watchSubstatus: '',
      handoverDateFrom: '',
      handoverDateTo: '',
      contractYear: [],
    });

  };


  const toggleArrayFilter = (key: 'projectType' | 'contractYear', value: string) => {
    const currentArray = filters[key];
    const newArray = currentArray.includes(value)
      ? currentArray.filter(v => v !== value)
      : [...currentArray, value];
    handleFilterChange(key, newArray);
  };

  const setDateFilter = (key: 'handoverDateFrom' | 'handoverDateTo', date: Date | undefined) => {
    onFiltersChange({
      ...filters,
      [key]: formatDateForBackend(date) ?? '',
    });
  };

  const clearDateRange = () => {
    onFiltersChange({ ...filters, handoverDateFrom: '', handoverDateTo: '' });
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.searchQuery) count++;
    if (filters.projectType.length > 0) count++;
    if (filters.contractYear.length > 0) count++;
    if (filters.company) count++;
    if (filters.projectMark) count++;
    if (filters.projectRoleUserIds && filters.projectRoleUserIds.length > 0) count++;
    if (filters.projectRoleTypes && filters.projectRoleTypes.length > 0) count++;
    if (filters.lifecycleStage) count++;
    if (filters.watchSubstatus) count++;
    if (filters.handoverDateFrom || filters.handoverDateTo) count++;

    return count;
  };

  const activeFiltersCount = getActiveFiltersCount();

  return (
    <Card>
      <CardContent className="p-4 space-y-4">
        {/* Search Input + right slot */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Tìm kiếm dự án bảo trì..."
            value={filters.searchQuery}
            onChange={(e) => handleFilterChange('searchQuery', e.target.value)}
            className="pl-10"
          />
          {filters.searchQuery && (
            <Button
              variant="ghost"
              size="sm"
              className="absolute right-1 top-1/2 transform -translate-y-1/2 px-2 h-8"
              onClick={() => clearFilter('searchQuery')}
            >
              <X className="h-3 w-3" />
            </Button>
          )}
          </div>
          {rightSlot}
        </div>

        {/* Company Filter */}
        <div>
          <div className="flex flex-wrap gap-2">
            {['ENP', 'Solid', 'Unassigned'].map((company) => (
              <Badge
                key={company}
                variant={filters.company === company ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => handleFilterChange('company', filters.company === company ? '' : company)}
              >
                {company === 'Unassigned' ? 'Chưa phân' : company}
              </Badge>
            ))}
          </div>
        </div>

        {/* Contract Year Tags */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm font-medium text-muted-foreground">Năm HĐ</span>
            {filters.contractYear.length > 0 && (
              <Badge variant="secondary" className="text-xs">
                {filters.contractYear.length}
              </Badge>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {(contractYearOptions || []).map((year) => (
              <Badge
                key={year}
                variant={filters.contractYear.includes(year) ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => toggleArrayFilter('contractYear', year)}
              >
                {year}
                {filters.contractYear.includes(year) && (
                  <X className="ml-1 h-3 w-3" />
                )}
              </Badge>
            ))}
          </div>
        </div>

        {/* Project Type Tags */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            {filters.projectType.length > 0 && (
              <Badge variant="secondary" className="text-xs">
                {filters.projectType.length}
              </Badge>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {(accessibleTypes || []).map((type) => (
              <Badge
                key={type}
                variant={filters.projectType.includes(type) ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => toggleArrayFilter('projectType', type)}
              >
                {type}
                {filters.projectType.includes(type) && (
                  <X className="ml-1 h-3 w-3" />
                )}
              </Badge>
            ))}
          </div>
        </div>

        {/* Thành viên + Chức năng + Mark - Same Row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {/* Project Role User Filter - MultiSelect */}
          {isProjectRolesEnabled && (
            <div>
              <MultiSelect
                options={(projectRoleUsers || []).map(u => ({ value: u.id, label: u.name }))}
                value={filters.projectRoleUserIds || []}
                onChange={(ids) => handleFilterChange('projectRoleUserIds', ids)}
                placeholder="Thành viên..."
                className="w-full"
                showSelectAll
                searchPlaceholder="Tìm thành viên..."
              />
            </div>
          )}

          {/* Project Role Type Filter - MultiSelect */}
          {isProjectRolesEnabled && (
            <div>
              <MultiSelect
                options={(projectRoleTypes || []).map(t => ({ value: t, label: t }))}
                value={filters.projectRoleTypes || []}
                onChange={(types) => handleFilterChange('projectRoleTypes', types)}
                placeholder="Chức năng..."
                className="w-full"
                showSelectAll
                searchPlaceholder="Tìm chức năng..."
              />
            </div>
          )}

          {/* Mark Filter */}
          <div>
            <div className="flex flex-wrap gap-1.5">
              {PROJECT_MARKS.map((mark) => (
                <Badge
                  key={mark}
                  variant={filters.projectMark === mark ? 'default' : 'outline'}
                  className={`cursor-pointer text-xs ${filters.projectMark === mark ? PROJECT_MARK_BADGE_COLORS[mark] : ''}`}
                  onClick={() => handleFilterChange('projectMark', filters.projectMark === mark ? '' : mark)}
                >
                  {PROJECT_MARK_LABELS[mark]}
                  {filters.projectMark === mark && (
                    <X className="ml-1 h-3 w-3" />
                  )}
                </Badge>
              ))}
            </div>
          </div>
        </div>




        {/* Handover Date Range Filter */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground">Ngày bàn giao:</span>
          {(['handoverDateFrom', 'handoverDateTo'] as const).map((key) => {
            const value = filters[key];
            const parsed = value ? parseISO(value) : undefined;
            const placeholder = key === 'handoverDateFrom' ? 'Từ ngày' : 'Đến ngày';
            return (
              <Popover key={key}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className={cn(
                      'h-9 justify-start text-left font-normal w-[150px]',
                      !value && 'text-muted-foreground'
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {parsed ? format(parsed, 'dd/MM/yyyy') : placeholder}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={parsed}
                    onSelect={(d) => setDateFilter(key, d ?? undefined)}
                    initialFocus
                    locale={vi}
                    weekStartsOn={1}
                    className={cn('p-3 pointer-events-auto')}
                  />
                </PopoverContent>
              </Popover>
            );
          })}
          {(filters.handoverDateFrom || filters.handoverDateTo) && (
            <Button variant="ghost" size="sm" className="h-9 px-2" onClick={clearDateRange}>
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>

        {/* Active Filters & Clear All */}
        {activeFiltersCount > 0 && (
          <div className="flex items-center justify-between pt-2 border-t">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>Hiển thị với {activeFiltersCount} bộ lọc</span>
              <Badge variant="secondary">
                {activeFiltersCount}
              </Badge>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={clearAllFilters}
              className="h-8"
            >
              Xóa tất cả
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
